import { useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader, Empty } from "../components/common";
import SimpleCrud, { FormFields } from "../components/SimpleCrud";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { money, fdate } from "../lib/format";

const CUR = [{ value: "USD", label: "USD" }, { value: "MXN", label: "MXN" }];

function PriceLists({ services, countries }) {
  const { t } = useT();
  const { can } = useAuth();
  const { data, reload } = useFetch("/price-lists");
  const [f, setF] = useState(null);
  const svc = Object.fromEntries((services || []).map((s) => [s.id, s]));
  const save = async () => {
    try {
      if (f.id) await api.put(`/price-lists/${f.id}`, f); else await api.post("/price-lists", f);
      toast.success(t("common.saved")); setF(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const items = f?.items || [];
  const updItem = (i, p) => setF({ ...f, items: items.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  return (
    <div data-testid="price-lists">
      <div className="mb-3 flex justify-end">{can("master:write") && <Button size="sm" data-testid="new-price-list-btn" onClick={() => setF({ currency: "USD", country: "*", items: [] })}><Plus className="h-4 w-4" />{t("cat.newList")}</Button>}</div>
      <div className="overflow-x-auto rounded-md border border-neutral-200 bg-white">
        <table className="dense-table w-full">
          <thead><tr><th>{t("common.name")}</th><th>{t("common.country")}</th><th>{t("q.currency")}</th><th>{t("cat.serviceType")}</th><th>{t("cat.validFrom")}</th><th>{t("cat.validTo")}</th><th>#</th><th /></tr></thead>
          <tbody>
            {(data || []).map((p) => (
              <tr key={p.id} className="cursor-pointer" onClick={() => can("master:write") && setF({ ...p })} data-testid={`price-list-row-${p.id}`}>
                <td className="font-semibold">{p.name}</td><td>{p.country === "*" ? "LATAM" : p.country}</td><td>{p.currency}</td><td>{p.service_type}</td>
                <td>{fdate(p.valid_from)}</td><td>{fdate(p.valid_to)}</td><td>{(p.items || []).length}</td>
                <td className="text-right">{can("master:write") && <Button size="sm" variant="outline" data-testid={`price-list-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" />{t("common.edit")}</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.length && <Empty>{t("common.empty")}</Empty>}
      </div>
      <Dialog open={!!f} onOpenChange={() => setF(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="price-list-dialog">
          <DialogHeader><DialogTitle>{f?.id ? f.name : t("cat.newList")}</DialogTitle></DialogHeader>
          {f && <>
            <FormFields f={f} setF={setF} fields={[{ key: "name", label: t("common.name"), required: true, wide: true },
              { key: "country", label: t("common.country"), type: "select", placeholder: undefined, options: [{ value: "*", label: "LATAM (*)" }, ...countries] },
              { key: "currency", label: t("q.currency"), type: "select", placeholder: undefined, options: CUR }, { key: "service_type", label: t("cat.serviceType") },
              { key: "machine_type", label: t("cat.machineType") }, { key: "valid_from", label: t("cat.validFrom"), type: "date" }, { key: "valid_to", label: t("cat.validTo"), type: "date" }]} />
            <h4 className="mt-2 text-xs font-bold uppercase tracking-wider">{t("cat.items")}</h4>
            <table className="dense-table w-full">
              <thead><tr><th>{t("cat.code")}</th><th>{t("cat.cost")}</th><th>{t("cat.price")}</th><th /></tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td><select className="cell-input" value={it.service_id || ""} onChange={(e) => updItem(i, { service_id: e.target.value })} data-testid={`pl-item-service-${i}`}>
                      <option value="">—</option>{(services || []).map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}</select></td>
                    <td><input type="number" className="cell-input num" value={it.unit_cost ?? ""} onChange={(e) => updItem(i, { unit_cost: Number(e.target.value) })} data-testid={`pl-item-cost-${i}`} /></td>
                    <td><input type="number" className="cell-input num" value={it.unit_price ?? ""} onChange={(e) => updItem(i, { unit_price: Number(e.target.value) })} data-testid={`pl-item-price-${i}`} /></td>
                    <td><button onClick={() => setF({ ...f, items: items.filter((_, j) => j !== i) })} className="text-neutral-400 hover:text-[#db0720]"><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button size="sm" variant="outline" data-testid="pl-add-item-btn" onClick={() => setF({ ...f, items: [...items, { service_id: "", unit_cost: 0, unit_price: 0 }] })}><Plus className="h-3.5 w-3.5" />{t("common.add")}</Button>
            <p className="text-[11px] text-neutral-500">{t("cat.frozenNote")}</p>
          </>}
          <DialogFooter><Button variant="outline" onClick={() => setF(null)}>{t("common.cancel")}</Button><Button data-testid="price-list-save-btn" onClick={save}>{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {svc && null}
    </div>
  );
}

export default function Catalog() {
  const { t } = useT();
  const { data: services } = useFetch("/services");
  const { data: countries } = useFetch("/countries");
  const copts = (countries || []).map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));
  const types = ["labor", "travel", "expense", "service", "parts"].map((x) => ({ value: x, label: t(`lt.${x}`) }));
  return (
    <div>
      <PageHeader title={t("cat.title")} kicker={t("nav.group.master")} />
      <Tabs defaultValue="services">
        <TabsList className="mb-4 rounded-md border border-neutral-200 bg-white p-1">
          <TabsTrigger value="services" data-testid="tab-services" className="text-xs font-semibold data-[state=active]:bg-[#db0720] data-[state=active]:text-white">{t("cat.services")}</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-price-lists" className="text-xs font-semibold data-[state=active]:bg-[#db0720] data-[state=active]:text-white">{t("cat.pricelists")}</TabsTrigger>
        </TabsList>
        <TabsContent value="services">
          <SimpleCrud testid="services" endpoint="/services" defaults={{ currency: "USD", line_type: "service", active: true }}
            columns={[{ key: "code", label: t("cat.code"), render: (r) => <span className="font-mono font-semibold">{r.code}</span> }, { key: "name", label: t("common.name") },
              { key: "line_type", label: t("ws.type"), render: (r) => t(`lt.${r.line_type}`) }, { key: "unit", label: t("cat.unit") },
              { key: "default_cost", label: t("cat.cost"), num: true, render: (r) => money(r.default_cost, r.currency) }, { key: "default_price", label: t("cat.price"), num: true, render: (r) => money(r.default_price, r.currency) }]}
            fields={[{ key: "code", label: t("cat.code"), required: true }, { key: "line_type", label: t("ws.type"), type: "select", required: true, options: types },
              { key: "name", label: t("common.name"), required: true, wide: true }, { key: "name_en", label: t("cat.nameEn"), wide: true }, { key: "unit", label: t("cat.unit") },
              { key: "currency", label: t("q.currency"), type: "select", required: true, options: CUR }, { key: "default_cost", label: t("cat.cost"), type: "number" },
              { key: "default_price", label: t("cat.price"), type: "number" }, { key: "service_type", label: t("cat.serviceType") }]} />
        </TabsContent>
        <TabsContent value="lists"><PriceLists services={services} countries={copts} /></TabsContent>
      </Tabs>
    </div>
  );
}
